import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Printer } from 'lucide-react';

const GOLD = '#b8941f';
const REGISTER_URL = 'https://appguardiansfit.com/granada';

const Page = ({ children, dataTestId }) => (
  <section
    className="dossier-page bg-white text-zinc-900 mx-auto shadow-xl print:shadow-none"
    style={{ width: '210mm', minHeight: '296mm', padding: '16mm 18mm' }}
    data-testid={dataTestId}
  >
    {children}
  </section>
);

const Header = ({ tagline }) => (
  <header className="flex items-start justify-between border-b-2 pb-4" style={{ borderColor: GOLD }}>
    <div>
      <p className="text-2xl font-extrabold tracking-[0.25em]" style={{ color: GOLD }}>GUARDIANSFIT</p>
      <p className="text-sm text-zinc-600 mt-1">{tagline}</p>
    </div>
    <div className="text-right text-xs text-zinc-500">
      <p className="font-semibold text-zinc-700">appguardiansfit.com</p>
      <p>Entrenamiento estructurado con seguimiento humano</p>
    </div>
  </header>
);

const Bullet = ({ children }) => (
  <li className="flex gap-3">
    <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ background: GOLD }} />
    <p className="text-[13px] leading-relaxed">{children}</p>
  </li>
);

const Steps = () => (
  <div className="grid grid-cols-3 gap-4 mt-3">
    {[
      ['1', 'Escanea el QR', 'Accede al cuestionario inicial.'],
      ['2', 'Responde el cuestionario', 'Indica tu nivel, objetivo, disponibilidad y posibles limitaciones.'],
      ['3', 'Empieza', 'Entrena con un plan claro y seguimiento real.'],
    ].map(([n, t, d]) => (
      <div key={n} className="border border-zinc-200 rounded-lg p-3">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-white font-bold text-sm" style={{ background: GOLD }}>{n}</span>
        <p className="font-semibold text-[13px] mt-2">{t}</p>
        <p className="text-[12px] text-zinc-600">{d}</p>
      </div>
    ))}
  </div>
);

const Pricing = () => (
  <div className="mt-3 grid grid-cols-3 gap-4">
    {[
      ['Free', '0€', 'Acceso inicial a la plataforma.', false],
      ['Pro Mensual', '9,99€/mes', '14 días gratis · acceso completo.', true],
      ['Pro Anual', '79€/año', 'Ahorra frente al plan mensual.', false],
    ].map(([name, price, d, featured]) => (
      <div key={name} className="rounded-lg border p-4" style={{ borderColor: featured ? GOLD : '#e4e4e7', background: featured ? '#fdf8e9' : 'white' }}>
        <p className="text-xs uppercase tracking-widest text-zinc-500">{name}</p>
        <p className="text-2xl font-extrabold mt-1" style={{ color: featured ? GOLD : '#18181b' }}>{price}</p>
        <p className="text-[11px] text-zinc-600 mt-2 leading-relaxed">{d}</p>
      </div>
    ))}
  </div>
);

const Footer = () => (
  <footer className="mt-auto pt-5 border-t border-zinc-200 flex items-center justify-between">
    <div className="text-[12px] text-zinc-600 leading-relaxed">
      <p className="font-bold text-zinc-800 text-[13px]">Sin permanencia. Cancela en un clic.</p>
      <p>Empieza hoy: <strong>appguardiansfit.com/granada</strong></p>
    </div>
    <div className="border-4 border-white rounded-lg" style={{ outline: `2px solid ${GOLD}` }}>
      <QRCodeSVG value={REGISTER_URL} size={92} level="M" style={{ display: 'block', margin: 6 }} />
    </div>
  </footer>
);

const SectionTitle = ({ children }) => (
  <h2 className="text-sm font-bold uppercase tracking-[0.18em] mt-6 mb-2" style={{ color: GOLD }}>{children}</h2>
);

export default function DossierPage() {
  return (
    <div className="min-h-screen bg-zinc-200 py-8 print:py-0 print:bg-white" data-testid="dossier-page">
      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          .no-print { display: none !important; }
          .dossier-page { page-break-after: always; }
          body { background: white; }
        }
      `}</style>

      <div className="no-print max-w-[210mm] mx-auto mb-6 flex items-center justify-between bg-zinc-900 text-white rounded-xl px-6 py-4">
        <div>
          <p className="font-bold">Dossier comercial · GUARDIANSFIT</p>
          <p className="text-xs text-zinc-400">3 páginas: Programa Senior · Programa Atlético Funcional · Cómo empezar.</p>
        </div>
        <Button onClick={() => window.print()} className="bg-amber-500 hover:bg-amber-400 text-black font-bold" data-testid="dossier-print-btn">
          <Printer className="h-4 w-4 mr-2" /> Imprimir / Guardar PDF
        </Button>
      </div>

      <div className="space-y-8 print:space-y-0 flex flex-col items-center">
        {/* ============ PÁGINA 1 · PROGRAMA SENIOR ============ */}
        <Page dataTestId="dossier-page-senior">
          <div className="flex flex-col" style={{ minHeight: '264mm' }}>
            <Header tagline="Programa Senior · Fuerza, movilidad y autonomía a partir de los 60" />

            <h1 className="text-3xl font-extrabold mt-6 leading-snug">
              La fuerza que te mantiene independiente<br />no se conserva sola. Se entrena.
            </h1>
            <p className="text-[13px] text-zinc-700 mt-3 leading-relaxed">
              A partir de los 60, la fuerza, la movilidad y la autonomía necesitan trabajarse de forma
              progresiva. Subir escaleras, cargar la compra o levantarse de una silla también dependen de
              la condición física. GUARDIANSFIT ofrece un programa estructurado para entrenar con
              seguridad, orden y seguimiento humano, adaptado al punto de partida de cada persona.
            </p>

            <SectionTitle>Qué incluye el programa</SectionTitle>
            <ul className="space-y-2.5">
              <Bullet>Un plan progresivo por fases, con ejercicios seleccionados para mejorar fuerza, movilidad y autonomía con seguridad.</Bullet>
              <Bullet>Cada ejercicio incluye explicación, series, repeticiones, descansos y notas claras para saber qué hacer.</Bullet>
              <Bullet>El programa progresa por fases para que el entrenamiento no se quede estancado.</Bullet>
              <Bullet>Seguimiento humano y revisión de dudas dentro de la app. No es solo automatización.</Bullet>
            </ul>

            <SectionTitle>Cómo empezar</SectionTitle>
            <Steps />

            <SectionTitle>Precios</SectionTitle>
            <Pricing />

            <Footer />
          </div>
        </Page>

        {/* ============ PÁGINA 2 · PROGRAMA ATLÉTICO FUNCIONAL ============ */}
        <Page dataTestId="dossier-page-atletico">
          <div className="flex flex-col" style={{ minHeight: '264mm' }}>
            <Header tagline="Programa Atlético Funcional · Para adultos activos" />

            <h1 className="text-3xl font-extrabold mt-6 leading-snug">
              Entrenas, pero ¿con un plan de verdad<br />o repitiendo siempre lo mismo?
            </h1>
            <p className="text-[13px] text-zinc-700 mt-3 leading-relaxed">
              Muchas personas entrenan con constancia, pero dejan de progresar por falta de estructura:
              mismas rutinas, mismas cargas y poca planificación. GUARDIANSFIT ordena el entrenamiento
              por fases para que cada sesión tenga sentido dentro de un proceso.
            </p>

            <SectionTitle>Qué incluye el programa</SectionTitle>
            <ul className="space-y-2.5">
              <Bullet>Programación funcional por fases, con progresión de volumen, intensidad y dificultad según el objetivo.</Bullet>
              <Bullet>Sesiones claras: ejercicios, series, repeticiones, descansos, tempo y notas de ejecución.</Bullet>
              <Bullet>Progresión por fases para evitar el estancamiento y mantener una evolución ordenada.</Bullet>
              <Bullet>Seguimiento humano para revisar dudas, ajustes técnicos o cambios necesarios dentro del proceso.</Bullet>
            </ul>

            <SectionTitle>Cómo empezar</SectionTitle>
            <Steps />

            <SectionTitle>Precios</SectionTitle>
            <Pricing />

            <Footer />
          </div>
        </Page>

        {/* ============ PÁGINA 3 · CÓMO EMPEZAR (resumen público) ============ */}
        <Page dataTestId="dossier-page-como-empezar">
          <div className="flex flex-col" style={{ minHeight: '264mm' }}>
            <Header tagline="Cómo empezar en GUARDIANSFIT" />

            <h1 className="text-3xl font-extrabold mt-6 leading-snug">
              Tres pasos para entrenar con orden.
            </h1>
            <p className="text-[13px] text-zinc-700 mt-3 leading-relaxed">
              GUARDIANSFIT propone un método sencillo para empezar: entender tu punto de partida,
              recibir un programa estructurado y contar con seguimiento humano durante el proceso.
            </p>

            <SectionTitle>El proceso</SectionTitle>
            <div className="grid grid-cols-1 gap-3 mt-3">
              {[
                {
                  n: '1',
                  t: 'Escanea el QR',
                  d: 'Accedes al cuestionario inicial desde el móvil, sin instalar nada.',
                },
                {
                  n: '2',
                  t: 'Responde el cuestionario',
                  d: 'Indicas tu nivel, tu objetivo, tu disponibilidad y las limitaciones que existan (lesiones, cirugías, molestias).',
                },
                {
                  n: '3',
                  t: 'Empieza tu programa',
                  d: 'Recibes un plan estructurado con cada ejercicio explicado. Un entrenador real revisa tu progreso dentro de la app.',
                },
              ].map((s) => (
                <div key={s.n} className="border border-zinc-200 rounded-lg p-4 flex items-start gap-4">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-full text-white font-bold" style={{ background: GOLD }}>{s.n}</span>
                  <div>
                    <p className="font-bold text-[14px]">{s.t}</p>
                    <p className="text-[12.5px] text-zinc-700 leading-relaxed mt-1">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>

            <SectionTitle>Qué caracteriza a GUARDIANSFIT</SectionTitle>
            <ul className="space-y-2.5">
              <Bullet>Programa estructurado por fases con progresión ordenada.</Bullet>
              <Bullet>Seguimiento humano y revisión real, no solo automatización.</Bullet>
              <Bullet>Vídeos explicativos para cada ejercicio dentro de la app.</Bullet>
              <Bullet>Sin permanencia. Cancela cuando quieras.</Bullet>
            </ul>

            <SectionTitle>Precios</SectionTitle>
            <Pricing />

            <Footer />
          </div>
        </Page>
      </div>
    </div>
  );
}
